import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { setToken } from './services/api';
import { MainLayout } from './components/layout';
import { PWAInstallPrompt } from './components/common';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Machines from './pages/Machines';
import Bodyweight from './pages/Bodyweight';
import Workouts from './pages/Workouts';
import WorkoutSession from './pages/WorkoutSession';
import FloorPlan from './pages/FloorPlan';
import Community from './pages/Community';
import WorkoutBuilder from './pages/WorkoutBuilder';
import TrainingPlan from './pages/TrainingPlan';
import AiSettings from './pages/AiSettings';
import Logout from './pages/Logout';

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600">Betöltés...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Guest Route wrapper (for login/register)
const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { loadUser } = useAuthStore();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('authToken');

    if (authToken) {
      setToken(authToken);
      useAuthStore.setState({ token: authToken, isAuthenticated: true });
      params.delete('authToken');
      const query = params.toString();
      const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', cleanUrl);
    }

    loadUser();
  }, [loadUser]);

  return (
    <>
      {/* PWA Install Prompt for mobile users */}
      <PWAInstallPrompt />
      
      <Routes>
      {/* Guest routes */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <Login />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <Register />
          </GuestRoute>
        }
      />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/machines" element={<Machines />} />
        <Route path="/bodyweight" element={<Bodyweight />} />
        <Route path="/history" element={<Workouts />} />
        <Route path="/workout" element={<WorkoutSession />} />
        <Route path="/floorplan" element={<FloorPlan />} />
        <Route path="/community" element={<Community />} />
        <Route path="/workout-builder" element={<WorkoutBuilder />} />
        <Route path="/training-plan" element={<TrainingPlan />} />
        <Route path="/ai-settings" element={<AiSettings />} />
      </Route>

      {/* Logout route */}
      <Route path="/logout" element={<Logout />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
